// テンプレート型は @juro/shared (packages/shared/src/template-types.ts) が真実の源。
// createBlankTemplate はテンプレートエディタ専用のユーティリティ。
import type { TemplateDef, GridBlock, AnyBlock } from "@juro/shared";

export type SkeletonOptions = {
  name?: string;
  /** 書類種別。タイトル文言と源泉所得税行の有無に使う */
  docType?: string;
  orientation?: "portrait" | "landscape";
  /** false なら骨格なし（タイトルのみ）で開始 */
  withSkeleton?: boolean;
};

/** 源泉所得税の行を骨格に含める書類種別（business-logic.md §2） */
const WITHHOLDING_DOC_TYPES = new Set([
  "judicial_invoice",
  "surveyor_invoice",
  "guarantee_invoice",
  "estimate",
]);

function titleTextFor(docType: string | undefined): string {
  if (docType === "estimate") return "御　見　積　書";
  if (docType?.endsWith("_invoice")) return "請　求　書";
  return "書　　類";
}

/**
 * 「空のテンプレートから」用の最小骨格。
 * 完全な白紙は組み立てが大変なので、ヘッダー帯（タイトル/発行日/宛名/発行者情報）＋
 * 明細グリッド（明細行を動的展開）＋合計欄の標準構成から始める。
 * withSkeleton: false でタイトルのみの白紙になる。
 */
export function createSkeletonTemplate(options: SkeletonOptions = {}): TemplateDef {
  const {
    name = "新しいテンプレート",
    docType,
    orientation = "portrait",
    withSkeleton = true,
  } = options;

  const title: AnyBlock = {
    id: "blk_title",
    type: "title",
    text: titleTextFor(docType),
    style: { font_size: 26, align: "center", bold: true, letter_spacing: "0.3em" },
  };

  const base: TemplateDef = {
    schema_version: 2,
    name,
    page_config: {
      size: "A4",
      orientation,
      margins: { top: 18, right: 15, bottom: 18, left: 15 },
    },
    body: { blocks: [title] },
  };

  if (!withSkeleton) return base;

  const settlementRows = [
    { label: "合　計", formula_text: "①＋②", ref: "③", value_key: "computed.gross" },
    { label: "消　費　税", formula_text: "①×10%", ref: "④", value_key: "computed.consumption_tax" },
    ...(docType && WITHHOLDING_DOC_TYPES.has(docType)
      ? [{ label: "源泉所得税", formula_text: "(①−10,000)×10.21/100", ref: "⑤", value_key: "computed.withholding_tax" }]
      : []),
    {
      label: docType === "estimate" ? "お見積金額" : "差引受領額",
      formula_text: docType && WITHHOLDING_DOC_TYPES.has(docType) ? "③＋④−⑤" : "③＋④",
      value_key: "computed.total",
    },
  ];

  const blocks: AnyBlock[] = [
    {
      id: "blk_header_band",
      type: "free_band",
      height: 52,
      row_unit: 4,
      children: [
        { pos: { col: 0, colSpan: 7, row: 0, rowSpan: 3 }, block: title },
        {
          pos: { col: 7, colSpan: 5, row: 0, rowSpan: 2 },
          block: {
            id: "blk_date",
            type: "text",
            text: "{{invoice.date}}",
            style: { font_size: 10, align: "right" },
          },
        },
        {
          pos: { col: 0, colSpan: 6, row: 3, rowSpan: 3 },
          block: {
            id: "blk_recipient",
            type: "recipient",
            data_key: "client.name",
            honorific: "様",
            underline: true,
            style: { font_size: 14 },
          },
        },
        {
          pos: { col: 6, colSpan: 6, row: 2, rowSpan: 11 },
          block: {
            id: "blk_issuer_info",
            type: "issuer_info",
            lines: [
              { kind: "office_name_short" },
              { kind: "office_name_main" },
              { kind: "postal" },
              { kind: "address" },
              { kind: "issuer_name", bold: true },
              { kind: "tel" },
              { kind: "fax" },
              { kind: "registration_number" },
            ],
            style: { font_size: 9, align: "right" },
          },
        },
      ],
    },
    { id: "blk_divider_1", type: "divider" },
    {
      id: "blk_grid_main",
      type: "grid",
      label: "明細",
      bordered: true,
      min_visual_rows: 10,
      data_source: "line_items",
      columns: [
        { id: "item_date",   header: "日　付", width: 58,  format: "date",     align: "center" },
        { id: "description", header: "内　容", width: 180, format: "text",     align: "left"   },
        { id: "fee_amount",  header: "報酬額", width: 90,  format: "currency", align: "right"  },
        { id: "tax_amount",  header: "実費等", width: 100, format: "currency", align: "right"  },
      ],
      rows: [],
      summary: [
        {
          id: "s_sub",
          label: "小　　　計",
          emphasis: true,
          markers: { fee_amount: "①", tax_amount: "②" },
          cells: {
            fee_amount: "{{computed.fee_subtotal}}",
            tax_amount: "{{computed.tax_subtotal}}",
          },
        },
      ],
    },
    { id: "blk_spacer_1", type: "spacer", height: 4 },
    {
      id: "blk_settlement",
      type: "settlement",
      bordered: true,
      vertical_label: "合計",
      rows: settlementRows,
    },
    {
      id: "blk_note",
      type: "text",
      text: "{{notes}}",
      style: { font_size: 9 },
    },
  ];

  return { ...base, body: { blocks } };
}

export function createBlankTemplate(name = "新しいテンプレート"): TemplateDef {
  return {
    schema_version: 2,
    name,
    page_config: {
      size: "A4",
      orientation: "portrait",
      margins: { top: 20, right: 15, bottom: 15, left: 15 },
    },
    body: {
      blocks: [
        {
          id: "blk_title",
          type: "title",
          text: "請 求 書",
          style: { font_size: 20, align: "center", bold: true },
        },
        {
          id: "grid_main",
          type: "grid",
          columns: [
            { id: "category", header: "作業種別", width: 120 },
            { id: "qty", header: "件数", width: 42, format: "number" },
            { id: "base", header: "基準", width: 70, format: "currency" },
            { id: "rate", header: "加減率", width: 54, format: "percent" },
            {
              id: "amount",
              header: "計",
              width: 86,
              format: "currency",
              formula: "={row.qty} * {row.base} * (1 + {row.rate})",
            },
            { id: "note", header: "摘要", width: 110 },
          ],
          rows: [
            { id: "r_1", level: 1, label: "1 調査業務", is_section: true },
            {
              id: "r_1_1",
              level: 2,
              parent: "r_1",
              label: "1) 資料調査",
              is_subsection: true,
            },
            {
              id: "r_1_1_a",
              level: 3,
              parent: "r_1_1",
              label: "ア 公簿類",
              cells: { qty: 1, base: 10000, rate: 0, note: "" },
              editable: ["qty", "base", "rate", "note"],
            },
            {
              id: "r_subtotal_1",
              type: "subtotal",
              level: 2,
              parent: "r_1",
              label: "小計",
              cells: { amount: "=sum(rows[r_1.*].amount)" },
            },
          ],
          summary: [
            {
              id: "s_1",
              label: "合計報酬額",
              ref: "①",
              cells: { amount: "=sum(rows[*.subtotal].amount)" },
            },
            {
              id: "s_2",
              label: "消費税",
              ref: "②",
              cells: { amount: "=consumption_tax({s_1.amount})" },
            },
            {
              id: "s_3",
              label: "合計",
              ref: "③",
              emphasis: true,
              cells: { amount: "={s_1.amount} + {s_2.amount}" },
            },
          ],
        } as GridBlock,
        {
          id: "blk_note",
          type: "text",
          text: "※振込手数料はお客様にてご負担願います。",
          style: { font_size: 9 },
        },
      ],
    },
  };
}
